import type { StockMovementResource } from '@repo/api-client';
import {
  IconArrowBackUp,
  IconClipboardCheck,
  IconGlassOff,
  IconLeafOff,
  IconSearch,
  IconSearchOff,
  IconShoppingCart,
  IconTruckDelivery,
} from '@tabler/icons-react';

import { formatLevel } from './stock-levels';

/**
 * What moved, why, and what made it move — the three readings the movement log
 * turns into something a person can act on.
 *
 * Separated from the screen because each of them loses information if it is got
 * wrong in a cell, and none of the three is obvious:
 *
 * - **The sign is the movement.** There is no direction field (FR-502), so a
 *   quantity rendered without its sign is a number with the fact removed.
 * - **The reason is not a status.** Eight values, two of which — `SALE` and
 *   `STOCKTAKE_VARIANCE` — are both common and mean entirely different things,
 *   so they have to be told apart at a glance rather than read.
 * - **The cause is chosen by the reason, never sniffed.** See `causeOf`.
 */

/** The published set, straight off the generated model rather than restated. */
export type MovementReason = StockMovementResource['reason'];

/**
 * What a reason is called on screen.
 *
 * Sentence case and in the words somebody at the park would use, not the wire
 * constant. `STOCKTAKE_VARIANCE` is the one worth arguing about: "Variance"
 * alone is an accounting word for the difference between two numbers, and what
 * this row actually is, is the correction a count produced. "Counted" says what
 * happened; the cause beside it says which count.
 */
export function labelForReason(reason: MovementReason): string {
  switch (reason) {
    case 'SALE':
      return 'Sold';
    case 'REFUND_RETURN':
      return 'Returned';
    case 'DELIVERY':
      return 'Delivered';
    case 'BREAKAGE':
      return 'Broken';
    case 'SPOILAGE':
      return 'Spoiled';
    case 'LOSS':
      return 'Lost';
    case 'FOUND':
      return 'Found';
    case 'STOCKTAKE_VARIANCE':
      return 'Counted';
  }
}

/**
 * Every Tabler icon has this one type, so naming it off a concrete icon binds it
 * to the package's own shape without depending on a type alias the package may
 * or may not export.
 */
export type ReasonIcon = typeof IconShoppingCart;

/**
 * The mark that tells two reasons apart before either is read.
 *
 * **Icons and not colour, and that is the palette rather than a preference.**
 * `docs/ui-conventions.md`, Colour: Tea Garden Green and `destructive` are the
 * only chromatic colours in the interface, green is the one primary action on a
 * screen, and red means the system refused something. Eight reasons cannot be
 * eight colours without inventing seven, and none of these is a refusal — a
 * breakage is a fact somebody recorded, not an error. So the badge is `outline`
 * in every case and the silhouette carries the distinction.
 *
 * Chosen to be distinguishable in outline at 12px rather than to be literal, and
 * the two pairs are deliberate: `LOSS` and `FOUND` are a magnifier struck
 * through and a magnifier, because they are each other's opposite and are the
 * pair somebody scanning a column is most likely to confuse.
 */
export function iconForReason(reason: MovementReason): ReasonIcon {
  switch (reason) {
    case 'SALE':
      return IconShoppingCart;
    case 'REFUND_RETURN':
      return IconArrowBackUp;
    case 'DELIVERY':
      return IconTruckDelivery;
    case 'BREAKAGE':
      return IconGlassOff;
    case 'SPOILAGE':
      return IconLeafOff;
    case 'LOSS':
      return IconSearchOff;
    case 'FOUND':
      return IconSearch;
    case 'STOCKTAKE_VARIANCE':
      return IconClipboardCheck;
  }
}

/**
 * What made this movement happen, if anything did.
 *
 * `sale`, `refund` and `stocktake` each carry the id of the thing to reach;
 * `none` is a delivery or a correction, which have no cause row at all and are
 * the reason the three ids are nullable in the first place.
 */
export type MovementCause =
  | { readonly kind: 'sale'; readonly saleId: string }
  | { readonly kind: 'refund'; readonly saleRefundId: string }
  | { readonly kind: 'stocktake'; readonly stocktakeId: string }
  | { readonly kind: 'none' }
  | { readonly kind: 'unstated'; readonly reason: MovementReason };

/**
 * Which of the three causes this row names — decided by `reason`, and never by
 * looking to see which id happens to be filled in.
 *
 * The distinction matters and it is not stylistic. The document guarantees that
 * exactly one of `saleId` / `saleRefundId` / `stocktakeId` is non-null, that
 * *which* one agrees with `reason`, and that a `stock_movements_cause_matches_
 * reason` CHECK is what makes a disagreeing row unable to exist (FR-505). A
 * function that sniffed the populated field would be asking the database a
 * question the database has already answered — and on the day a fourth cause is
 * added it would silently pick the wrong branch for the new reason instead of
 * failing to compile here.
 *
 * `unstated` is the branch that cannot happen: the reason names a cause and the
 * id is null. It exists because the generated model types all three as `string |
 * null` and that is the only honest reading of the type — a `!` here would be a
 * runtime crash on a manager's screen for a contract violation the screen could
 * have simply reported. The row is still shown as it stands; nothing is hidden
 * or dropped on account of it.
 */
export function causeOf(movement: StockMovementResource): MovementCause {
  switch (movement.reason) {
    case 'SALE':
      return movement.saleId === null
        ? { kind: 'unstated', reason: movement.reason }
        : { kind: 'sale', saleId: movement.saleId };

    case 'REFUND_RETURN':
      return movement.saleRefundId === null
        ? { kind: 'unstated', reason: movement.reason }
        : { kind: 'refund', saleRefundId: movement.saleRefundId };

    case 'STOCKTAKE_VARIANCE':
      return movement.stocktakeId === null
        ? { kind: 'unstated', reason: movement.reason }
        : { kind: 'stocktake', stocktakeId: movement.stocktakeId };

    // A delivery and the four correction reasons. All three ids are null and
    // that is the whole answer: the movement *is* the record, entered by the
    // person named in the actor column, and there is no second row behind it to
    // reach. Not an omission and not something to apologise for on screen.
    case 'DELIVERY':
    case 'BREAKAGE':
    case 'SPOILAGE':
    case 'LOSS':
    case 'FOUND':
      return { kind: 'none' };
  }
}

/**
 * A movement as somebody reads it: `+12`, `−3`.
 *
 * **The plus is not decoration.** `quantity` is signed and the sign is the only
 * thing that says which way the goods went — the API has no direction field
 * (FR-502) — so a column that printed `12` and `−3` would be a column where the
 * ins are unmarked and the outs are marked, read at a glance as "3 of these are
 * a problem". Both directions are marked, or neither means anything.
 *
 * Built on `formatLevel` rather than beside it, which is the relationship the
 * two figures actually have: a level **is** the sum of these movements (FR-507),
 * so a level and a movement that disagreed about grouping or about what a minus
 * sign looks like would be two renderings of one arithmetic. That inherits the
 * true minus sign (U+2212, a digit's width in tabular figures, announced as
 * "minus"), the Indian grouping, and the rounding guard.
 *
 * Zero takes no sign. A movement of nothing is not a thing the API can produce —
 * every route that writes one refuses a zero quantity — but `number` admits it,
 * and `+0` on a screen is worse than the bare `0` that at least reads as the
 * nothing it is.
 */
export function formatMovementQuantity(quantity: number): string {
  // Rounded before the sign is decided, and not after. `formatLevel` rounds
  // whatever it is given, so testing the raw value would print `+0` for a
  // fractional 0.4 — a sign attached to a figure that is not there.
  const whole = Math.round(quantity);

  return `${whole > 0 ? '+' : ''}${formatLevel(whole)}`;
}
